// ./src/components/features/CreateEventWizard/steps/PollsStep.tsx
//
// "What will you ask guests to vote on?" — shown when the Polls & quizzes feature is on (ROADMAP A2b #62; owner,
// 2026-09-29). Polls and quiz questions are real rows on the event, made in the Polls page's own
// `PollQuestionEditDrawer` under a provider scoped by the event. Same list rules as the other wizard lists: the read's
// own failure, never the context's `lastError`.

import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { PollQuestionsProvider, usePollQuestionsContext } from '@/contexts';
import { useCreateGate, useToast } from '@/hooks';
import { PollQuestion, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { PollQuestionEditDrawer } from '@/components/features/PollQuestions';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { startersFor, unusedStarters } from '../wizardStarters';
import { useTypeIdeas } from '../useTypeIdeas';

const PollsStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { pollQuestions, list, isLoaded, remove } = usePollQuestionsContext();
  const gate = useCreateGate(FEATURE_CODES.ENGAGEMENT, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null; question?: string } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PollQuestion | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // ⓘ The TYPE's own ideas (#62 P6(h)) — a potluck's "Dessert", a book club's "Which book next?". One tap opens the
  //   real drawer with the question filled in; nothing is made until the host saves. Hidden once a row has that question.
  //   ⓘ No generic set here: a sign-up item or a poll means nothing without the type behind it.
  const typeIdeas = useTypeIdeas('polls');
  const starters = unusedStarters(startersFor(typeIdeas, [], 'ChartBar'), pollQuestions.map((row) => row.question));

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.polls.intro', {
          defaultValue:
            'Ask guests to vote — the menu, the music, a date — or play a quiz. Guests answer on the event page, and you see the results live.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.polls.title', { defaultValue: 'Polls & quiz questions' })}
        addLabel={t('events:wizard.polls.add', { defaultValue: 'Add a poll' })}
        onAdd={() => gate.canCreate && setDrawer({ recordId: null })}
        starters={starters.map((s) => ({ key: s.key, label: s.label, icon: s.icon, onClick: () => gate.canCreate && setDrawer({ recordId: null, question: s.label }) }))}
        gate={gate}
        gateWhat={t('events:wizard.polls.another', { defaultValue: 'another poll' })}
        disabled={saving}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.polls.loadFailed', { defaultValue: 'Could not load the polls' })}
        operation="pollQuestionList"
        emptyText={t('events:wizard.polls.none', { defaultValue: 'No polls yet.' })}
        isEmpty={pollQuestions.length === 0}
      >
        {pollQuestions.map((q) => (
          <WizardItemRow
            key={q.id}
            icon={q.isQuiz ? 'Puzzle' : 'ChartBar'}
            title={q.question ?? ''}
            subtitle={
              q.isQuiz
                ? t('events:wizard.polls.quiz', { defaultValue: 'Quiz question' })
                : t('events:wizard.polls.poll', { defaultValue: 'Poll' })
            }
            disabled={saving}
            onEdit={() => setDrawer({ recordId: q.id as string })}
            onRemove={() => setPendingRemove(q)}
          />
        ))}
      </WizardListSection>

      <PollQuestionEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        defaultQuestion={drawer?.question ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.polls.removeTitle', { defaultValue: 'Remove this poll?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.question ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const PollsStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <PollQuestionsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <PollsStepBody eventId={createdEventId} />
    </PollQuestionsProvider>
  );
};
